/**
 * `folderDto.ts` — the project-FOLDER cloud contract.
 *
 * Unity reference: `Models/Data/FolderData.cs` (the record) and the folder
 * half of `Models/StorageModel/AWS/DynamoClient.cs:447-680` (the rows).
 *
 * A folder is TWO kinds of Dynamo row sharing one partition:
 *
 *   folder row   pk = "{customerId}:folder"   sk = "!folder:{folderId}"
 *   map row      pk = "{customerId}:folder"   sk = "!folder:{folderId}:map:{projectId}"
 *
 * Membership lives ONLY in the map rows — the folder row carries no project
 * list — which is what makes moving a project two batched writes rather than a
 * read-modify-write of a list attribute that two clients could clobber.
 *
 * **One query returns both kinds**, which is not obvious and is worth stating
 * because it looks like a bug in Unity's reader otherwise (`GetFoldersByCustomer`
 * runs a single `!folder:{MinGuid}` → `!folder:{MaxGuid}` range and then splits
 * the results by looking for `map` in the sort key, `DynamoClient.cs:501-556`).
 * It works because a map sk is its folder's sk plus a suffix beginning `:`
 * (0x3A), and every character a GUID can hold is either a hex digit (0x30-0x39,
 * 0x61-0x66) or `-` (0x2D) — so `!folder:{guid}:map:…` sorts after
 * `!folder:{guid}` and, for any guid below the `ffff…` ceiling, still below the
 * range end. Do not "fix" the range to exclude map rows: the single query IS
 * the design, and splitting it in two would double the round trips.
 *
 * Timestamps: Unity writes `DateTime.ToString()` (culture-formatted) into `S`
 * attributes, and reads them back through `DateTime.TryParse`, which accepts
 * ISO-8601 — so the web writes ISO here like every other row it owns
 * (`HttpStorageModel.updateProjectName`'s note). Do NOT confuse these with the
 * WIRE timestamps in `folderWire.ts`, which are epoch-millisecond longs.
 */

/** Dynamo attribute shape of a folder row, exactly as `ToFolderItem` writes it. */
export interface FolderDto {
  /** Dynamo partition key, present on read. */
  readonly pk?: string;
  /** Dynamo sort key, present on read — the folder/map discriminator. */
  readonly sk?: string;
  readonly ID: string;
  readonly Name: string;
  readonly CreatorID: string;
  /** `S`, parsed leniently. Unity default on read: `DateTime.UtcNow`. */
  readonly CreatedTS?: string;
  /** `S`. Unity default on read: the row's own `CreatedTS`. */
  readonly ModifiedTS?: string;
  readonly Author?: string;
  /** `BOOL`. Account-wide folder; Unity read default `false`. */
  readonly IsShared?: boolean;
}

/** Dynamo attribute shape of a folder→project map row (`ToFolderToProjectMapItem`). */
export interface FolderProjectMapDto {
  readonly pk?: string;
  readonly sk?: string;
  readonly ProjectID: string;
}

export function folderPk(customerId: string): string {
  return `${customerId}:folder`;
}

export function folderSk(folderId: string): string {
  return `!folder:${folderId}`;
}

export function folderMapSk(folderId: string, projectId: string): string {
  return `!folder:${folderId}:map:${projectId}`;
}

/**
 * Unity's discriminator is `sortKey.S.Contains("map")` (`DynamoClient.cs:522`).
 * Taken literally that is unsound — a folder GUID cannot contain "map" (no `m`
 * or `p` in hex), but the check would also match a future sk segment that did.
 * We test for the `:map:` SEGMENT instead, which selects exactly the same rows
 * today and cannot be tripped by an id.
 */
const K_MAP_SEGMENT = ":map:";

export function isFolderMapSk(sk: string | undefined): boolean {
  return typeof sk === "string" && sk.includes(K_MAP_SEGMENT);
}

/**
 * Folder id out of a map sk. Unity splits on `:` and takes index 1
 * (`DynamoClient.cs:531`), which is the same thing given the sk grammar.
 */
export function folderIdFromMapSk(sk: string): string {
  const parts = sk.split(":");
  return parts.length > 1 ? parts[1] : "";
}

/** Marshalled item for `PutItem`, matching `ToFolderItem` attribute-for-attribute. */
export function folderDtoToItem(
  customerId: string,
  folder: FolderDto,
): Record<string, { S: string } | { BOOL: boolean }> {
  return {
    pk: { S: folderPk(customerId) },
    sk: { S: folderSk(folder.ID) },
    ID: { S: folder.ID },
    Name: { S: folder.Name ?? "" },
    CreatorID: { S: folder.CreatorID ?? "" },
    CreatedTS: { S: folder.CreatedTS ?? new Date().toISOString() },
    ModifiedTS: { S: folder.ModifiedTS ?? folder.CreatedTS ?? new Date().toISOString() },
    Author: { S: folder.Author ?? "" },
    IsShared: { BOOL: folder.IsShared === true },
  };
}

/** Marshalled item for a map row (`ToFolderToProjectMapItem`). */
export function folderMapItem(
  customerId: string,
  folderId: string,
  projectId: string,
): Record<string, { S: string }> {
  return {
    pk: { S: folderPk(customerId) },
    sk: { S: folderMapSk(folderId, projectId) },
    ProjectID: { S: projectId },
  };
}

/**
 * One folder as the app sees it: the row plus the membership joined from its
 * map rows. Unity's `FolderInfo` (`Models/CustomerModel/FolderInfo.cs`) wraps
 * `FolderData` the same way and additionally caches resolved `ProjectInfo`s;
 * the web keeps only the ids here and resolves against the catalog in the list
 * model, so there is one source of truth for a project's metadata.
 */
export interface FolderInfo {
  readonly id: string;
  readonly name: string;
  /** Cognito sub of the creator (`FolderData.CreatorID`). Drives `canEditFolder`. */
  readonly creatorId: string;
  /** Display name of the creator (`FolderData.Author`, = `UserInfo.NameTag`). */
  readonly author: string;
  /** Owning account. Drives `canMoveToFolder` and the cross-account guard. */
  readonly customerId: string;
  /** `FolderData.IsSharedFolder` — visible account-wide even when empty. */
  readonly isSharedFolder: boolean;
  /** ISO-8601. */
  readonly createdAt: string;
  /** ISO-8601 — the row's OWN timestamp; see `folderModifiedAt` for the
   *  effective one Unity displays. */
  readonly modifiedAt: string;
  /** Project ids from the map rows, in query order. */
  readonly projectIds: ReadonlyArray<string>;
}

export function mapFolderDtoToInfo(dto: FolderDto, customerId: string): FolderInfo {
  // An unreadable stamp is passed through RAW rather than replaced with "now".
  // Unity writes these with a bare `DateTime.ToString()`, i.e. in the WRITER's
  // culture, so a row created on a German or UK machine (`13.08.2025 10:23:45`)
  // is unparseable by `Date.parse` here. Substituting the current time would
  // re-evaluate on every refresh and pin that folder to the top of the Modified
  // AND Created sorts forever, while the desktop client — which parses its own
  // culture fine — shows it in its true place. Passed through, it fails
  // `Date.parse` at sort time and lands at 0 = oldest, which is exactly how the
  // grid already treats an unreadable PROJECT stamp (`useProjectList.timestamp`),
  // so the two kinds of tile at least agree.
  const createdAt = normalizeTimestamp(dto.CreatedTS) ?? dto.CreatedTS ?? "";
  return {
    id: dto.ID,
    name: dto.Name ?? "",
    creatorId: dto.CreatorID ?? "",
    author: dto.Author ?? "",
    customerId,
    isSharedFolder: dto.IsShared === true,
    createdAt,
    // Unity's read default for a missing `ModifiedTS` is the row's `CreatedTS`
    // (`DynamoClient.cs:459`), not "now" — a folder nobody has touched must not
    // float to the top of a Modified sort on every load. Same raw-passthrough
    // rule as `createdAt` above for an unparseable value.
    modifiedAt: normalizeTimestamp(dto.ModifiedTS) ?? dto.ModifiedTS ?? createdAt,
    projectIds: [],
  };
}

/**
 * The timestamp the UI shows for a folder. Unity's `FolderInfo.ModifiedTimestampUtc`
 * getter (`FolderInfo.cs:32-42`) returns the MAX of the folder row's own stamp
 * and every contained project's — so a folder sorts by how recently anything
 * inside it changed, which is the only reading that makes a Modified sort mean
 * the same thing for both card kinds.
 */
export function folderModifiedAt(
  folder: FolderInfo,
  childModifiedAtIso: ReadonlyArray<string | undefined>,
): string {
  let bestIso = folder.modifiedAt;
  let bestMs = Date.parse(folder.modifiedAt);
  if (!Number.isFinite(bestMs)) bestMs = 0;
  for (const iso of childModifiedAtIso) {
    if (!iso) continue;
    const ms = Date.parse(iso);
    if (Number.isFinite(ms) && ms > bestMs) {
      bestMs = ms;
      bestIso = iso;
    }
  }
  return bestIso;
}

/**
 * Accept whatever the row holds and normalize to ISO, or `undefined` when it is
 * unparseable. Unity-written rows carry `DateTime.ToString()` in the WRITER's
 * culture ("8/13/2026 10:23:45 AM" on a US machine, "13.08.2026 10:23:45" on a
 * German one) — `Date.parse` handles the US form and returns NaN for several
 * others, so an unreadable stamp must degrade to the caller's default rather
 * than poison a sort with NaN.
 */
function normalizeTimestamp(raw: string | undefined): string | undefined {
  if (!raw) return undefined;
  const ms = Date.parse(raw);
  return Number.isFinite(ms) ? new Date(ms).toISOString() : undefined;
}
